import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Loader2, AlertCircle } from 'lucide-react';
import { Query, RequesterInfo } from '../types';
import { pushDataLayer, getTrackingCookies } from '../utils/gtm';

const fmtCOP = (n: number) => new Intl.NumberFormat('es-CO').format(n);

interface Props {
  queries: Query[];
  requesterInfo: RequesterInfo;
  onSubmit: (info: RequesterInfo) => void;
  onBack: () => void;
}

export default function Step2({ queries, requesterInfo, onSubmit, onBack }: Props) {
  const total = queries.reduce((s, q) => s + q.amount, 0);

  const handleContinue = () => {
    pushDataLayer('form_submit_step2', {
      user: {
        first_name: requesterInfo.firstName,
        last_name: requesterInfo.lastName,
        doc_type: requesterInfo.docType,
        doc_number: requesterInfo.docNumber,
        email: requesterInfo.email,
        phone: requesterInfo.phone,
      },
      queries: queries.map(q => ({
        doc_type: q.docType, doc_number: q.docNumber,
        nombre: q.nombreCompleto || '', motivo: q.motivoLabel,
      })),
      tracking: getTrackingCookies(),
    });
    onSubmit(requesterInfo);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Query summary */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
          Resumen de consultas
        </h3>
        {queries.map((q) => {
          const isPending = q.verifyStatus === 'pending';
          const isVerified = q.verifyStatus === 'verified';
          return (
            <div key={q.id} className="rounded-2xl border p-4 mb-3" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    {isPending
                      ? <Loader2 size={13} className="text-[#2563EB] animate-spin flex-shrink-0" />
                      : isVerified
                      ? <CheckCircle2 size={13} className="text-[#10B981] flex-shrink-0" />
                      : <AlertCircle size={13} className="text-[#B45309] flex-shrink-0" />}
                    <span className={`text-xs font-semibold ${isPending ? 'text-[#2563EB]' : isVerified ? 'text-[#10B981]' : 'text-[#B45309]'}`}>
                      {isPending ? 'Verificando...' : isVerified ? 'Verificado' : 'No verificado'}
                    </span>
                  </div>
                  <p className="font-bold truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                    {q.nombreCompleto || `${q.docTypeLabel} · ${q.docNumber}`}
                  </p>
                  {q.nombreCompleto && (
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {q.docTypeLabel} · <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{q.docNumber}</span>
                    </p>
                  )}
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{q.motivoLabel}</p>
                </div>
                <span className="font-bold text-sm flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-primary)' }}>
                  ${fmtCOP(q.amount)}
                </span>
              </div>
            </div>
          );
        })}
        <div className="flex justify-between items-center px-1 mt-2">
          <span className="font-bold" style={{ color: 'var(--text-primary)' }}>Total</span>
          <span className="font-bold text-xl text-[#2563EB]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            ${fmtCOP(total)} COP
          </span>
        </div>
      </div>

      {/* Disclaimer + Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 border mb-4"
        style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-md)' }}
      >
        <p className="text-xs mb-5 flex items-start gap-1.5" style={{ color: 'var(--text-secondary)' }}>
          <Lock size={12} className="flex-shrink-0 mt-0.5" />
          Tus datos están protegidos bajo la Ley 1581 de 2012 (Habeas Data). Al continuar confirmas los términos y condiciones.
        </p>

        <div className="flex gap-3">
          <motion.button
            type="button"
            data-testid="back-btn-step2"
            whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl border-2 font-medium transition-all"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-surface)' }}
          >
            <ArrowLeft size={16} />
            Volver
          </motion.button>
          <motion.button
            data-testid="submit-step2-btn"
            whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
            onClick={handleContinue}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-white transition-all"
            style={{ backgroundColor: '#2563EB', boxShadow: '0 4px 20px rgba(37,99,235,0.25)' }}
          >
            Ir al pago seguro
            <ArrowRight size={18} />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
